<script setup lang="ts">
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { useSessions } from "~/composables/sessions/useSessions";
import StatusTag from "~/components/Common/StatusTag.vue";
import { useSessionIndividual } from "~/composables/sessions/useSessionIndividual";
import { useAuthStore } from "~/store/auth/auth.store";
import SessionAllowedAction from "./SessionAllowedAction.vue";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";
import { ItemType } from "~/utils/enums/common.enums";
import AppButton from "~/components/Common/Buttons/AppButton.vue";

const session = useSessions();
const router = useRouter();
const authStore = useAuthStore();
const sessionsStore = useSessionsStore();
const sessionIndividual = useSessionIndividual();
const sessionDetailsStore = useSessionDetailsStore();

const data = computed(() => sessionIndividual.getSessionHeading());
async function showMySession() {
  const sourceId = sessionIndividual.sourceId.value;
  const sourceType = sessionIndividual.sourceType.value;
  if (!sourceId || !sourceType) return;

  const sessionUrl = `/sessions?id=${sourceId}&type=${sourceType}`;
  const routeName = (router.currentRoute.value.name as string) ?? "";

  // Keep the learning path open behind the side-modal: show the session in a new tab.
  if (routeName.includes("learning-path")) {
    await navigateTo(sessionUrl, { open: { target: "_blank" } });
    return;
  }

  // Only /sessions mounts the session-detail modal: elsewhere (/registrations) the store's
  // query-only push would leave us on the current page with nothing to show.
  if (!sessionDetailsStore.sessionViewModal) {
    sessionsStore.trainingPlanSessionIndividual = null;
    sessionsStore.showSessionModal = false;
    await navigateTo(sessionUrl);
    return;
  }

  await sessionDetailsStore.showMySession(sourceId, sourceType as ItemType);
  sessionsStore.trainingPlanSessionIndividual = null;
}
</script>

<template>
  <div v-if="data" class="session_heading__container">
    <div class="session_heading__user_icon">
      <span v-if="data.firstname && data.lastname">
        {{ data.firstname.charAt(0) + data.lastname.charAt(0) }}
      </span>
      <span v-else>-</span>
    </div>
    <div class="session_heading__user_detail">
      <h1 class="heading_1 text-center">
        {{ data.fullname ?? data.email ?? "-" }}
      </h1>
      <h3 class="heading_3_med text-center">
        {{ `${data.training ?? "-"} - ${data.partner ?? "-"}` }}
      </h3>
    </div>
    <div class="flex items-center relative">
      <StatusTag :tag="session.statusMapping(data.status!)" />
      <!-- On a pending registration, the status badge is not editable (AC-SUIVI.11) -->
      <SessionAllowedAction
        v-if="
          sessionsStore.trainingPlanSessionIndividual?.allowedActions.length &&
          data.status !== 'canceled' &&
          !sessionsStore.trainingPlanSessionIndividual?.dateChoice
        "
        :status="data.status as string"
      />
    </div>
    <!--
      A pending registration carries no session yet: its own action row ("Choisir une date" /
      "Modifier le prix" / "Voir détails") replaces this CTA, which would otherwise be a fourth
      button pointing at the same carrier as "Voir détails".
    -->
    <div
      v-if="
        (authStore.isCompanyAdmin || authStore.isGroupAdmin) &&
        !sessionsStore.trainingPlanSessionIndividual?.dateChoice
      "
      class="flex flex-row gap-[16px]"
    >
      <AppButton label="Voir Session" icon="link" @click="showMySession" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.session_heading__container {
  @apply flex flex-col items-center mt-[16px] mb-4 gap-[16px];
}

.session_heading__user_detail {
  @apply grid justify-center items-center;
}

.session_heading__user_icon {
  @apply flex justify-center items-center rounded-full w-16 h-16;
  background: var(--grey-light);
  color: var(--text-tertiary-color);
  border: 2px solid var(--grey-lightest);
}
</style>
